import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { Subscription } from 'rxjs';
import { PodUploadService } from 'src/app/services/proof-of-delivery/pod-upload.service';

type PodDocumentType = 'collection' | 'delivery';

@Component({
  selector: 'app-public-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, PdfJsViewerModule],
  templateUrl: './public-upload.component.html',
  styleUrls: ['./public-upload.component.scss']
})
export class PublicUploadComponent implements OnInit, OnDestroy {
  token: string = '';
  files: File[] = [];
  isDragOver: boolean = false;
  isUploading: boolean = false;
  isVerifying: boolean = false;
  isLoadingExistingDocument: boolean = false;
  tokenValid: boolean = false;
  showConfirmation: boolean = true;
  confirmationType: PodDocumentType | null = null;
  showReview: boolean = false;
  existingDocumentUnavailable: boolean = false;
  selectedPreviewIndex: number = 0;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error' = 'idle';
  statusMessage: string = '';
  validationErrors: { [key: string]: string[] } = {};
  bookingInfo: {
    booking_id?: number;
    email?: string;
    expires_at?: string;
    collection_uploaded?: boolean;
    delivery_uploaded?: boolean;
    both_uploaded?: boolean;
    is_expired?: boolean;
    can_upload?: boolean;
  } = {};
  existingDocument: File | null = null;

  private readonly allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
  private readonly maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
  private readonly previewUrls = new Map<File, string>();
  private existingDocumentSubscription: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private podUploadService: PodUploadService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    if (this.token) {
      this.verifyToken();
    } else {
      this.setError('No upload token provided.');
    }
  }

  ngOnDestroy(): void {
    this.existingDocumentSubscription?.unsubscribe();
    this.previewUrls.forEach(url => URL.revokeObjectURL(url));
  }

  private verifyToken(): void {
    this.isVerifying = true;
    this.statusMessage = 'Verifying upload link...';

    this.podUploadService.verifyToken(this.token).subscribe({
      next: (response) => {
        this.isVerifying = false;
        if (response.success) {
          this.tokenValid = true;
          this.bookingInfo = response.data || {};
          this.statusMessage = '';
        } else {
          this.tokenValid = false;
          this.setError(response.message);
        }
      },
      error: (error) => {
        this.isVerifying = false;
        this.tokenValid = false;
        
        if (error.status === 403) {
          this.setError('Invalid or expired upload link.');
        } else if (error.status === 500) {
          this.setError(error.error?.message || 'Token verification failed. Please try again.');
        } else {
          this.setError('Unable to verify upload link. Please try again.');
        }
      }
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canUpload) {
      return;
    }
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (!this.canUpload) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (!this.canUpload) {
      element.value = '';
      return;
    }

    if (element.files) {
      this.handleFiles(Array.from(element.files));
      element.value = '';
    }
  }

  private handleFiles(newFiles: File[]): void {
    const knownFiles = new Set(this.files.map(file => this.fileIdentity(file)));
    const validFiles = newFiles.filter(file => {
      if (!this.validateFile(file)) {
        return false;
      }

      const identity = this.fileIdentity(file);
      if (knownFiles.has(identity)) {
        return false;
      }

      knownFiles.add(identity);
      return true;
    });
    this.files = [...this.files, ...validFiles];
    this.clearStatus();
  }

  private fileIdentity(file: File): string {
    return [file.name, file.size, file.type, file.lastModified].join(':');
  }

  private validateFile(file: File): boolean {
    if (!this.allowedTypes.includes(file.type)) {
      this.setError(`File "${file.name}" is not a supported format. Only JPG, PNG, and PDF files are allowed.`);
      return false;
    }

    if (file.size > this.maxFileSize) {
      this.setError(`File "${file.name}" is too large. Maximum file size is 10MB.`);
      return false;
    }

    return true;
  }

  removeFile(index: number): void {
    const [removedFile] = this.files.splice(index, 1);
    if (removedFile) {
      this.releasePreviewUrl(removedFile);
    }
    
    // Adjust selected preview index if necessary
    if (this.showReview) {
      if (this.selectedPreviewIndex >= this.reviewFiles.length) {
        this.selectedPreviewIndex = Math.max(0, this.reviewFiles.length - 1);
      }
      
      // If no files left, go back to upload
      if (this.reviewFiles.length === 0) {
        this.showReview = false;
      }
    }
    
    this.clearStatus();
  }

  uploadFiles(): void {
    if (!this.canUpload) {
      this.setError('This upload link has expired. Existing documents are available in read-only mode.');
      return;
    }

    if (this.files.length === 0) {
      this.setError('Please select at least one file to upload.');
      return;
    }

    if (!this.confirmationType) {
      this.setError('Document type not specified.');
      return;
    }
    const documentType = this.confirmationType;

    const bookingId = this.bookingInfo.booking_id;
    if (!bookingId) {
      this.setError('Booking information is unavailable.');
      return;
    }

    this.isUploading = true;
    this.uploadStatus = 'uploading';
    this.statusMessage = 'Uploading files...';
    this.validationErrors = {};

    this.podUploadService.uploadFilesWithExisting(
      this.token,
      this.existingDocument,
      this.files,
      documentType
    ).subscribe({
      next: (response) => {
        this.isUploading = false;
        if (response.success) {
          this.files.forEach(file => this.releasePreviewUrl(file));
          this.files = [];
          this.clearExistingDocument();
          this.markDocumentTypeAsUploaded(documentType);
          this.reloadExistingDocumentIntoReview(documentType, response.message);
        } else {
          this.uploadStatus = 'error';
          this.statusMessage = response.message;
          this.validationErrors = response.errors || {};
        }
      },
      error: (error) => {
        this.isUploading = false;
        this.uploadStatus = 'error';
        
        if (error.status === 403) {
          this.statusMessage = 'Invalid or expired upload link.';
        } else if (error.status === 422) {
          this.statusMessage = 'File validation failed.';
          this.validationErrors = error.error?.errors || {};
        } else if (error.status === 500) {
          this.statusMessage = error.error?.message || 'Upload failed. Please try again.';
        } else {
          this.statusMessage = 'An unexpected error occurred. Please try again.';
        }
      }
    });
  }

  private setError(message: string): void {
    this.uploadStatus = 'error';
    this.statusMessage = message;
  }

  private clearStatus(): void {
    if (this.uploadStatus !== 'uploading') {
      this.uploadStatus = 'idle';
      this.statusMessage = '';
      this.validationErrors = {};
    }
  }

  getFileSizeString(size: number): string {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
    return (size / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getFileIcon(file: File): string {
    if (file.type.startsWith('image/')) return 'bi-file-earmark-image';
    if (file.type === 'application/pdf') return 'bi-file-earmark-pdf';
    return 'bi-file-earmark';
  }

  hasValidationErrors(): boolean {
    return Object.keys(this.validationErrors).length > 0;
  }

  getValidationErrorsList(): string[] {
    const errors: string[] = [];
    Object.values(this.validationErrors).forEach(errorArray => {
      errors.push(...errorArray);
    });
    return errors;
  }

  confirmCollection(): void {
    this.selectDocumentType('collection');
  }

  confirmDelivery(): void {
    this.selectDocumentType('delivery');
  }

  private selectDocumentType(documentType: PodDocumentType): void {
    this.cancelExistingDocumentLoad();
    this.clearExistingDocument();
    this.files.forEach(file => this.releasePreviewUrl(file));
    this.files = [];
    this.confirmationType = documentType;
    this.showConfirmation = false;
    this.showReview = false;
    this.existingDocumentUnavailable = false;
    this.clearStatus();

    this.loadExistingDocumentIntoReview(documentType);
  }

  private reloadExistingDocumentIntoReview(
    documentType: PodDocumentType,
    successMessage: string
  ): void {
    this.confirmationType = documentType;
    this.showConfirmation = false;
    this.showReview = false;
    this.loadExistingDocumentIntoReview(documentType, successMessage);
  }

  private loadExistingDocumentIntoReview(
    documentType: PodDocumentType,
    successMessage?: string
  ): void {
    this.cancelExistingDocumentLoad();
    this.existingDocumentUnavailable = false;

    const bookingId = this.bookingInfo.booking_id;
    if (!bookingId) {
      this.existingDocumentUnavailable = true;
      this.setError('Booking information is unavailable.');
      return;
    }

    this.isLoadingExistingDocument = true;
    this.existingDocumentSubscription = this.podUploadService
      .getExistingDocument(bookingId, documentType, this.token)
      .subscribe({
      next: (document) => {
        this.isLoadingExistingDocument = false;
        const mimeType = document.type || 'application/pdf';
        const extension = this.extensionForMimeType(mimeType);
        this.existingDocument = new File(
          [document],
          `${documentType}_proof_of_delivery.${extension}`,
          { type: mimeType }
        );
        this.selectedPreviewIndex = 0;
        this.showReview = true;
        this.existingDocumentUnavailable = false;
        if (successMessage) {
          this.uploadStatus = 'success';
          this.statusMessage = successMessage;
          this.validationErrors = {};
        } else {
          this.clearStatus();
        }
      },
      error: (error) => {
        this.isLoadingExistingDocument = false;
        const documentWasUploaded = this.hasUploadedDocument(documentType);
        if (error.status === 404 && !documentWasUploaded && this.canUpload) {
          this.existingDocumentUnavailable = false;
          this.clearStatus();
        } else if (error.status === 404) {
          this.existingDocumentUnavailable = true;
          this.setError(documentWasUploaded
            ? `The existing ${documentType} document could not be loaded. Please try again.`
            : `No existing ${documentType} document is available.`);
        } else {
          this.existingDocumentUnavailable = documentWasUploaded || !this.canUpload;
          this.setError(this.canUpload
            ? 'Unable to load the existing document. You can still upload a new file.'
            : 'Unable to load the existing document.');
        }
      }
    });
  }

  private hasUploadedDocument(documentType: PodDocumentType): boolean {
    return documentType === 'collection'
      ? this.bookingInfo.collection_uploaded === true
      : this.bookingInfo.delivery_uploaded === true;
  }

  private markDocumentTypeAsUploaded(documentType: PodDocumentType): void {
    if (documentType === 'collection') {
      this.bookingInfo.collection_uploaded = true;
    } else {
      this.bookingInfo.delivery_uploaded = true;
    }

    this.bookingInfo.both_uploaded =
      this.bookingInfo.collection_uploaded === true &&
      this.bookingInfo.delivery_uploaded === true;
  }

  private extensionForMimeType(mimeType: string): string {
    const extensions: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png'
    };

    return extensions[mimeType.toLowerCase()] || 'bin';
  }

  goBackToConfirmation(): void {
    this.cancelExistingDocumentLoad();
    this.clearExistingDocument();
    this.files.forEach(file => this.releasePreviewUrl(file));
    this.showConfirmation = true;
    this.confirmationType = null;
    this.showReview = false;
    this.existingDocumentUnavailable = false;
    this.files = [];
    this.clearStatus();
  }

  proceedToReview(): void {
    if (this.reviewFiles.length === 0) {
      this.setError('Please select at least one file to review.');
      return;
    }
    this.showReview = true;
    this.selectedPreviewIndex = 0;
    this.clearStatus();
  }

  goBackToUpload(): void {
    if (this.confirmationType && (this.existingDocument || this.hasUploadedDocument(this.confirmationType))) {
      this.loadExistingDocumentIntoReview(this.confirmationType);
      return;
    }

    this.showReview = false;
    this.clearStatus();
  }

  goBackFromReview(): void {
    this.goBackToConfirmation();
  }

  nextPreview(): void {
    if (this.selectedPreviewIndex < this.reviewFiles.length - 1) {
      this.selectedPreviewIndex++;
    }
  }

  previousPreview(): void {
    if (this.selectedPreviewIndex > 0) {
      this.selectedPreviewIndex--;
    }
  }

  selectPreview(index: number): void {
    this.selectedPreviewIndex = index;
  }

  get reviewFiles(): File[] {
    return this.existingDocument ? [this.existingDocument, ...this.files] : this.files;
  }

  isExistingReviewFile(index: number): boolean {
    return this.existingDocument !== null && index === 0;
  }

  get canUpload(): boolean {
    return this.bookingInfo.can_upload !== false;
  }

  removeReviewFile(index: number): void {
    const fileIndex = index - (this.existingDocument ? 1 : 0);
    if (fileIndex >= 0) {
      this.removeFile(fileIndex);
    }
  }

  getFilePreviewUrl(file: File): string {
    let previewUrl = this.previewUrls.get(file);
    if (!previewUrl) {
      previewUrl = URL.createObjectURL(file);
      this.previewUrls.set(file, previewUrl);
    }
    return previewUrl;
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }

  private clearExistingDocument(): void {
    if (this.existingDocument) {
      this.releasePreviewUrl(this.existingDocument);
      this.existingDocument = null;
    }
  }

  private cancelExistingDocumentLoad(): void {
    this.existingDocumentSubscription?.unsubscribe();
    this.existingDocumentSubscription = null;
    this.isLoadingExistingDocument = false;
  }

  private releasePreviewUrl(file: File): void {
    const previewUrl = this.previewUrls.get(file);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      this.previewUrls.delete(file);
    }
  }
}
